import { createContext, ReactNode, useContext } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ButtonLink from '@v2/shared/components/ButtonLink';
import TextButton from '@v2/shared/components/TextButton';
import { trackEvent } from '@v2/shared/lib/analytics';
import { cn } from '@v2/shared/lib/utils';

const MICRO = 'text-[11px] font-bold uppercase tracking-wider';

/**
 * What an anchored section has to clear to land below the fixed header, which is three
 * different heights -- 53px on a phone, 126px in the middle, 82px wide.
 */
export const ANCHOR_OFFSET = 'scroll-mt-14 md:scroll-mt-32 xl:scroll-mt-24';

/**
 * The card a link belongs to, so the link does not have to be told.
 *
 * Every action on this page reports which topic it was under, and there are seventeen of
 * them. Passing the title to each one is seventeen chances to pass the wrong one, and a
 * mislabelled analytics event is wrong in a way nobody sees until they read a report.
 */
const TopicContext = createContext<string>('');

/**
 * One GA4 event for everything on this page that offers a way out of writing a message.
 *
 * `topic` is the card, `action` the words on the link, and `subject` the reason it
 * selects when the link is one that opens the form -- 0 when it is a link that leaves.
 * One event rather than one per card, so a report can rank them against each other.
 */
export function trackTopicClick(topic: string, action: string, subject = 0): void {
  trackEvent('contact_topic_click', { topic, action, subject });
}

/**
 * Names the topic for links that report one without being a card -- the "Other topics"
 * row, which is a line of links rather than a card but is the same choice to a visitor.
 */
export function TopicGroup({ title, children }: { title: string; children: ReactNode }) {
  return <TopicContext.Provider value={title}>{children}</TopicContext.Provider>;
}

type TopicProps = {
  icon: IconProp;
  title: string;
  /** The one card the design pulls forward, because it is the message most people send. */
  hot?: boolean;
  id?: string;
  children: ReactNode;
  actions: ReactNode;
};

/** One of the four cards under "What do you need help with?". */
export default function Topic({ icon, title, hot, id, children, actions }: TopicProps) {
  return (
    <TopicContext.Provider value={title}>
      <div
        id={id}
        className={cn(
          'relative flex flex-col rounded-lg border border-solid p-5',
          ANCHOR_OFFSET,
          hot ? 'border-primary-100 bg-primary-50' : 'border-slate-200 bg-white'
        )}
      >
        {hot && (
          <span className={cn('absolute right-4 top-4 rounded-full bg-white px-2.5 py-1 text-primary', MICRO)}>
            Most common
          </span>
        )}

        <span
          className={cn(
            'mb-3 flex h-9 w-9 flex-none items-center justify-center rounded-lg',
            hot ? 'bg-primary text-white' : 'bg-primary-50 text-primary'
          )}
        >
          <FontAwesomeIcon icon={icon} />
        </span>

        <h3 className="mb-2 mt-0 text-lg font-extrabold text-slate-900">{title}</h3>

        <div className="mb-4 text-sm leading-relaxed text-slate-700">{children}</div>

        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2">{actions}</div>
      </div>
    </TopicContext.Provider>
  );
}

/** The card's primary call to action, which goes to the account page that does the thing. */
export function TopicButtonLink({
  href,
  children,
  className,
}: {
  href: string;
  children: string;
  className?: string;
}) {
  const topic = useContext(TopicContext);

  return (
    <ButtonLink
      href={href}
      className={cn('normal-case tracking-normal', className)}
      onClick={() => trackTopicClick(topic, children)}
    >
      {children}
    </ButtonLink>
  );
}

/** A link out to the help article that answers the question in full. */
export function TopicLink({ href, children }: { href: string; children: string }) {
  const topic = useContext(TopicContext);

  return (
    <a href={href} className="text-sm font-semibold text-primary" onClick={() => trackTopicClick(topic, children)}>
      {children}
    </a>
  );
}

/**
 * The link that gives up on self-service and opens the form, with this card's reason
 * already chosen.
 *
 * A button rather than an anchor to `#message`: it selects the reason as well as moving,
 * and an anchor that also runs script is a link that does nothing for the people whose
 * script did not run. Nothing is lost without it -- the dropdown is right there.
 */
export function TopicMessageLink({
  reason,
  onPick,
  children,
}: {
  reason: number;
  onPick: (reason: number) => void;
  children: string;
}) {
  const topic = useContext(TopicContext);

  return (
    <TextButton
      color="secondary"
      size="sm"
      className="underline underline-offset-2"
      onClick={() => {
        trackTopicClick(topic, children, reason);
        onPick(reason);
      }}
    >
      {children}
    </TextButton>
  );
}
